import { FC, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import GenericCrudTable from "@/components/tables/genericCrudTable";
import type { CrudColumnDef } from "@/components/tables/crudColumnDef";
import { getRecordMetadataColumns } from "@/components/tables/recordMetadataColumns";
import { replaceDate } from "@/helper/dateHandling";
import DropDownSubjectSearch from "@/components/forms/inputs/dropDownSubjectSearch";
import styles from "@/components/accidentBook/accidentBook.module.css";
import { useConfirmationModal } from "@/context/confirmationModalContext";
import { api } from "@/helper/api";
import { useErrorModal } from "@/components/forms/errorHandling/useErrorModal";
import { Tooltip } from "@mui/material";
import GenericButton from "@/components/forms/inputs/button/genericButton";
import CheckIcon from "@mui/icons-material/Check";
import RemoveIcon from "@mui/icons-material/Remove";
import { useSelector } from "react-redux";
import { RootState } from "@/components/state/store";
import FileTableIcon from "@/components/files/fileTableIcon";
import ExportMenu, {
  ExportMenuItem,
} from "@/components/appointmentRequest/exportMenu";
import GenericNotification from "@/components/notification/genericNotification";
import { usePageHeading } from "@/helper/customHooks/usePageHeading";
import { ACCIDENT_BOOK_MODAL_STYLE } from "@/components/accidentBook/accidentBookModalStyle";
import { accidentBookEntityAdapter } from "@/components/accidentBook/accidentBookEntityAdapter";

interface AccidentBookTableProps {
  subdomain: string;
}

const AccidentBookTable: FC<AccidentBookTableProps> = ({ subdomain }) => {
  const isAdmin = subdomain === "admin";
  const { t } = useTranslation();
  usePageHeading(t("accidentbook"));
  const accidentBookSubjectSearchref = useRef(null);
  const { showConfirmationModalAsync } = useConfirmationModal();

  const [options] = useState<any[]>([]);
  const [subjectId, setSubjectId] = useState<string>("");
  const [refreshTable, setRefreshTable] = useState<boolean>(false);
  const { handleError, errorModal } = useErrorModal();
  const [successForNotification, setSuccessForNotification] =
    useState<boolean>(false);
  const [notificationMessage, setNotificationMessage] = useState<any>("");
  const [notificationVariant, setNotificationVariant] = useState<
    "success" | "error" | "warning" | "info" | undefined
  >();

  const selectedChildCompanySubjectId: any = useSelector(
    (state: RootState) => state.parent.subjectId
  );

  const handleNotification = (
    variant: "success" | "error" | "warning" | "info",
    message: string,
    success: boolean
  ) => {
    setNotificationMessage(message);
    setSuccessForNotification(success);
    setNotificationVariant(variant);
  };

  const resetStateOnCloseNotification = () => {
    setSuccessForNotification(false);
  };

  const handleSubjectSelect = (selectedOption: any) => {
    if (
      selectedOption &&
      typeof selectedOption === "object" &&
      selectedOption.value
    ) {
      setSubjectId(selectedOption.value);
    }
  };

  const resetAsyncInput = () => {
    setSubjectId("");
  };

  const accidentBookColumns = useMemo<CrudColumnDef[]>(
    () => [
      {
        accessorKey: "id",
        header: t("id"),
        id: "id",
        enableHiding: isAdmin,
        enableGlobalFilter: false,
      },
      {
        header: t("approved_on"),
        id: "approved_on",
        accessorKey: "approved_on",
        filterVariant: "date",
        sortingFn: "datetime",
        accessorFn: (row: any) => {
          if (row.approved_on !== null) {
            return (
              <span style={{ display: "flex", gap: "1rem" }}>
                <CheckIcon sx={{ color: "#008c7d" }} />
                {replaceDate(row.approved_on)}
              </span>
            );
          } else {
            return (
              <span>
                <RemoveIcon sx={{ color: "#dc3545" }} />
              </span>
            );
          }
        },
      },
      {
        header: t("approved_by"),
        accessorKey: "approved_by",
        customColumnFilterId: "approved_by_name",
        id: "approved_by",
        Cell: ({ cell }: any) => {
          return <span>{cell.getValue()?.name}</span>;
        },
      },
      {
        header: t("first_name"),
        accessorKey: "first_name",
        id: "first_name",
        required: true,
        accessorFn: (row: any) => {
          return row?.first_name;
        },
        enableForCreate: true,
      },
      {
        header: t("last_name"),
        accessorKey: "last_name",
        id: "last_name",
        required: true,
        accessorFn: (row: any) => {
          return row?.last_name;
        },
        enableForCreate: true,
      },
      {
        header: t("phone"),
        accessorKey: "phone",
        id: "phone",
        enableForCreate: true,
      },
      {
        header: t("department"),
        id: "department",
        accessorKey: "department",
        enableForCreate: true,
        required: true,
      },
      {
        header: t("activity"),
        accessorKey: "activity",
        id: "activity",
        accessorFn: (row: any) => {
          return row?.activity;
        },
        enableForCreate: true,
        required: true,
      },
      {
        header: t("accident_datetime"),
        accessorKey: "accident_datetime",
        id: "accident_datetime",
        Cell: ({ cell }: any) => {
          return <span>{replaceDate(cell.getValue())}</span>;
        },
        filterVariant: "date",
        sortingFn: "datetime",
        enableForCreate: true,
        dateTimePicker: true,
      },
      {
        header: t("accident_location"),
        id: "accident_location",
        accessorKey: "accident_location",
        accessorFn: (row: any) => {
          return row?.accident_location;
        },
        enableForCreate: true,
        textarea: true,
        modalFullWidth: true,
      },
      {
        header: t("accident_description"),
        id: "accident_description",
        accessorKey: "accident_description",
        enableForCreate: true,
        textarea: true,
        modalFullWidth: true,
      },
      {
        header: t("accident_results_description"),
        id: "accident_results_description",
        accessorKey: "accident_results_description",
        enableForCreate: true,
        textarea: true,
        modalFullWidth: true,
      },
      {
        header: t("witness_name"),
        id: "witness_name",
        accessorKey: "witness_name",
        enableForCreate: true,
        required: true,
      },
      {
        header: t("witness_phone"),
        id: "witness_phone",
        accessorKey: "witness_phone",
        enableForCreate: true,
      },
      {
        header: t("measures"),
        id: "measures",
        accessorKey: "measures",
        enableForCreate: true,
        textarea: true,
        modalFullWidth: true,
      },
      {
        header: t("first_responder_name"),
        id: "first_responder_name",
        accessorKey: "first_responder_name",
        enableForCreate: true,
        required: true,
      },
      {
        header: t("first_responder_phone"),
        id: "first_responder_phone",
        accessorKey: "first_responder_phone",
        enableForCreate: true,
      },
      {
        header: t("first_aid_datetime"),
        id: "first_aid_datetime",
        accessorKey: "first_aid_datetime",
        Cell: ({ cell }: any) => {
          return <span>{replaceDate(cell.getValue())}</span>;
        },
        filterVariant: "date",
        sortingFn: "datetime",
        enableForCreate: true,
        dateTimePicker: true,
      },
      {
        header: t("accident_insurance_doctor_visited"),
        id: "accident_insurance_doctor_visited",
        accessorKey: "accident_insurance_doctor_visited",
        accessorFn: (row: any) => {
          if (row.accident_insurance_doctor_visited) {
            return t("yes");
          } else return t("no");
        },
        filterVariant: "checkbox",
        enableForCreate: true,
        checkBox: true,
      },
      {
        header: t("emergency_address"),
        id: "emergency_address",
        accessorKey: "emergency_address",
        enableForCreate: true,
        textarea: true,
        modalFullWidth: true,
      },
      {
        header: t("additional_information"),
        id: "additional_information",
        accessorKey: "additional_information",
        enableForCreate: true,
        textarea: true,
        modalFullWidth: true,
      },
      // {
      //   header: t("approved_on"),
      //   accessorKey: "approved_on",
      //   id: "approved_on",
      //   filterVariant: "date",
      //   sortingFn: "datetime",
      //   enableColumnFilter: false,
      //   Cell: ({ cell }: any) => {
      //     return <span>{replaceDate(cell.getValue())}</span>;
      //   },
      // },
      ...(isAdmin 
        ? getRecordMetadataColumns(t, [
            "created_on",
            "created_by",
            "modified_on",
            "modified_by",
          ])
        : []),
    ],
    [isAdmin]
  );

  const additionalUrlParameters: any = {};

  if (subdomain === "admin") {
    additionalUrlParameters["company_subject_id"] = subjectId;
  }
  const triggerRefresh = () => {
    setRefreshTable((prev) => !prev);
  };

  const setAccidentApproved = async (row: any, skipQuestion = false) => {
    if (!skipQuestion) {
      const isConfirmed = await showConfirmationModalAsync(
        t("accident_setapproved_confirm")
      );
      if (!isConfirmed) return;
    }
    const additionalRouteParts: any = {};
    additionalRouteParts["mark_approved"] = "/";
    api.genericApiRequest({
      method: "put",
      entity: "accidentBook",
      entityId: row.id,
      ...((selectedChildCompanySubjectId !== "" ||
        selectedChildCompanySubjectId !== undefined) && {
        submitData: { impersonate_subject: selectedChildCompanySubjectId },
      }),
      additionalRouteParts: additionalRouteParts,
      successHandler: () => {
        triggerRefresh();
      },
      failHandler: (error: any) => {
        handleError(error);
      },
    });
  };
  const exportEntryAsPdf = (row: any) => {
    const entryId = row.id;

    api.genericApiRequest({
      method: "get",
      entity: "accidentBook",
      entityId: entryId,
      responseType: "blob",
      additionalRouteParts: { export_single_record: "/" },
      successHandler: (res: any) => {
        const blob = new Blob([res.data], { type: "application/pdf" });
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement("a");

        const disposition = res.headers["content-disposition"];
        const match = disposition?.match(/filename="?([^"]+)"?/);
        const filename = match?.[1] ?? "accidentBook_entry.pdf";

        link.href = url;
        link.download = filename;

        document.body.appendChild(link);
        link.click();
        link.remove();

        window.URL.revokeObjectURL(url);
      },
      failHandler: (error: any) => {
        console.log(error);
      },
    });
  };

  const customRowActions = (row: any) => {
    const shouldRenderCheckedIcon = row.original?.approved_on == null;

    return (
      <>
        <Tooltip
          arrow
          placement="top"
          title={t("export_pdf")}
          onClick={() => exportEntryAsPdf(row.original)}
        >
          <GenericButton variant="icon" color="iconBase">
            <FileTableIcon extension="pdf" />
          </GenericButton>
        </Tooltip>
        {shouldRenderCheckedIcon && (
          <Tooltip arrow placement="top" title={t("accident_setapproved")}>
            <span>
              <GenericButton
                onClick={() => setAccidentApproved(row.original)}
                variant="icon"
                color="iconBase"
              >
                <CheckIcon />
              </GenericButton>
            </span>
          </Tooltip>
        )}
      </>
    );
  };
  const exportMenuItems: ExportMenuItem[] = [
    {
      label: "export_all_as",
      format: "csv",
    },
    {
      label: "export_all_as",
      format: "xlsx",
      icon: "chart",
    },
  ];

  const additionalTopToolBarButtons = () => {
    return (
      <>
        <ExportMenu
          entity="accidentBook"
          menuItems={exportMenuItems}
          handleError={handleError}
          handleNotification={handleNotification}
          exportParameter="export_multiple_records"
          subjectId={subjectId}
        />
      </>
    );
  };

  return (
    <>
      {subdomain === "admin" && (
        <DropDownSubjectSearch
          wrapperClassName={styles.dropDownSubjectSearch}
          options={options}
          onChange={handleSubjectSelect}
          subdomain={subdomain}
          isClearable={true}
          resetInput={resetAsyncInput}
          autofocus={true}
          customRef={accidentBookSubjectSearchref}
          useGlobalState={true}
          queryParameter="companies"
        />
      )}
      {((subdomain === "admin" && subjectId !== "") ||
        subdomain === "manage" ||
        subdomain === "portal") && (
        <GenericCrudTable
          entity="accidentBook"
          entityAdapter={accidentBookEntityAdapter}
          columns={accidentBookColumns}
          searchId={subjectId}
          companySubjectId={subjectId}
          refreshTable={refreshTable}
          customRowActions={
            subdomain === "portal" ? undefined : customRowActions
          }
          additionalUrlParameter={additionalUrlParameters}
          additionalCustomTopToolbarElements={
            subdomain === "portal" ? undefined : additionalTopToolBarButtons
          }
          disableEditButtonForRow={
            subdomain === "portal"
              ? (row: any) => row.original?.approved_on != null
              : undefined
          }
          disableDeleteButtonForRow={
            subdomain === "portal"
              ? (row: any) => row.original?.approved_on != null
              : undefined
          }
          customModalStyle={ACCIDENT_BOOK_MODAL_STYLE}
          useTwoColumnModalLayout
          showColumnFilterbyDefault
          remountCrudModalOnPresetChange
        />
      )}
      {errorModal}

      <GenericNotification
        message={successForNotification ? notificationMessage : null}
        variant={notificationVariant}
        handleCloseSnackbar={resetStateOnCloseNotification}
      />
    </>
  );
};

export default AccidentBookTable;
